import Layout from "@/components/Layout";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import { GET_MY_CLIPS } from "@/queries/queries";
import { MyClipsQuery, MyClipsQueryVariables } from "@/generated/graphql";
import { useWindowScroll } from "@uidotdev/usehooks";
import ClipCard from "../components/ClipCard";
import { useAiAssistantAccess } from "@/hooks/use-ai-assistant-access";
import { Card } from "@/components/ui/card";
import { useClipVersionGroups } from "@/hooks/use-clip-versions";
import { collectHiddenVersionIds } from "@/lib/clips/groupClipVersions";
import { useTour } from "@reactour/tour";
import { withTour } from "@/components/withTour";
import { useClipPublishing } from "@/hooks/use-clip-publishing";
import { ClipPublishDialogs } from "@/components/publishing/ClipPublishDialogs";
import { SendToQueueDialog } from "@/components/publishQueues/SendToQueueDialog";
import { useSendToQueue } from "@/hooks/use-send-to-queue";
import { useHasFeature } from "@/lib/useHasFeature";
import { SOCIAL_PUBLISHING_FEATURE } from "@/lib/publishing/socialPublishingFeature";

const useTourSteps = () => {
  const { t } = useTranslation();
  return [
    {
      selector: '#clips-page-title',
      content: t('clips.tour.welcome'),
      position: 'bottom',
    },
    {
      selector: '#clips-grid',
      content: t('clips.tour.clipsGrid'),
      position: 'top',
    },
    {
      selector: '#help-button',
      content: t('clips.tour.helpButton'),
      position: 'bottom',
    },
  ];
};

const ClipsContent = () => {
  const { t } = useTranslation();
  const hasSocialPublishing = useHasFeature(SOCIAL_PUBLISHING_FEATURE);
  const { enabled: hasAiAssistant } = useAiAssistantAccess();
  const { setIsOpen, setCurrentStep } = useTour();
  const PAGE_SIZE = 24;
  const { data, loading, error, fetchMore } = useQuery<MyClipsQuery, MyClipsQueryVariables>(GET_MY_CLIPS, {
    variables: { limit: PAGE_SIZE, offset: 0 },
    fetchPolicy: 'cache-and-network',
  });
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const isFetchingMoreRef = useRef(false);
  const [scroll] = useWindowScroll();
  const scrollY = scroll?.y ?? 0;

  const clips = useMemo(() => data?.myClips ?? [], [data?.myClips]);
  // Version grouping: re-exported clips collapse into one card with a version badge
  const { getGroupForClip, groups: versionGroups } = useClipVersionGroups(clips);
  const visibleClips = useMemo(() => {
    const hidden = collectHiddenVersionIds(versionGroups);
    return clips.filter(clip => !hidden.has(clip.id));
  }, [clips, versionGroups]);

  const {
    hasProFeatures,
    getPublishStatus,
    getPublishFailReason,
    getPublishMediaKinds,
    showPublishProGate,
    setShowPublishProGate,
    publishTarget,
    setPublishTarget,
    resolveClipTitle,
    resolvePublishHandler,
    refetchPublishes,
  } = useClipPublishing(visibleClips);
  const {
    targetClipId: queueTargetClipId,
    handleSendToQueue,
    setOpen: setQueueDialogOpen,
  } = useSendToQueue({
    hasProFeatures,
    onProGate: () => setShowPublishProGate(true),
  });

  // Only blank the page on the very first load — background refetches keep the grid
  const isInitialLoading = loading && !data;

  const startTour = () => {
    setCurrentStep(0);
    setIsOpen(true);
  };

  const handleLoadMore = async () => {
    if (isFetchingMoreRef.current || loading || error || !hasMore) return;
    isFetchingMoreRef.current = true;
    setIsFetchingMore(true);
    try {
      const prevCount = clips.length;
      const res = await fetchMore({
        variables: {
          limit: PAGE_SIZE,
          offset: prevCount,
        },
      });
      const fetched = res.data?.myClips?.length ?? 0;
      if (fetched < PAGE_SIZE) setHasMore(false);
    } finally {
      isFetchingMoreRef.current = false;
      setIsFetchingMore(false);
    }
  };

  useEffect(() => {
    if (loading || !hasMore) return;
    const currentY = scrollY ?? 0;
    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    const docH = document.documentElement.scrollHeight || document.body.scrollHeight;
    const distanceFromBottom = docH - (currentY + viewportH);
    if (distanceFromBottom < 600) {
      handleLoadMore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollY, hasMore, loading]);

  useEffect(() => {
    if (!loading && data && clips.length < PAGE_SIZE) {
      setHasMore(false);
    }

  }, [loading, data, clips.length]);

  return (
    <Layout onStartTour={startTour}>
      <div className="flex-1 p-4 lg:p-6">
        <h2 id="clips-page-title" className="text-xl lg:text-2xl font-bold mb-4">{t('clips.title')}</h2>

        {isInitialLoading && (
          <div id="clips-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, idx) => (
              <Card key={`skeleton-${idx}`} className="p-3 sm:p-4 animate-pulse">
                <div className="aspect-video bg-muted rounded-lg mb-3" />
                <div className="h-3 bg-muted rounded w-3/4 mb-2" />
                <div className="h-3 bg-muted rounded w-full mb-1" />
                <div className="h-3 bg-muted rounded w-2/3" />
              </Card>
            ))}
          </div>
        )}

        {!isInitialLoading && error && (
          <div className="text-sm text-destructive">{t('clips.failedToLoadClips')}</div>
        )}

        {!isInitialLoading && !error && (
          <div id="clips-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {visibleClips.map(clip => (
              <ClipCard
                key={clip.id}
                clip={clip}
                versionGroup={getGroupForClip(clip.id)}
                // ClipCard owns render/expiry visibility from its polled clip.
                // These handlers represent page-level publishing capability.
                onSendToQueue={hasSocialPublishing ? handleSendToQueue : undefined}
                onSendToTikTok={resolvePublishHandler(true, 'tiktok')}
                onPublishToYouTube={resolvePublishHandler(true, 'youtube')}
                onPublishToInstagram={resolvePublishHandler(true, 'instagram')}
                onPublishToFacebook={resolvePublishHandler(true, 'facebook')}
                tiktokPublishStatus={getPublishStatus(clip.id, 'tiktok')}
                tiktokPublishFailReason={getPublishFailReason(clip.id, 'tiktok')}
                youtubePublishStatus={getPublishStatus(clip.id, 'youtube')}
                youtubePublishFailReason={getPublishFailReason(clip.id, 'youtube')}
                instagramPublishStatus={getPublishStatus(clip.id, 'instagram')}
                instagramPublishFailReason={getPublishFailReason(clip.id, 'instagram')}
                facebookPublishStatus={getPublishStatus(clip.id, 'facebook')}
                facebookPublishFailReason={getPublishFailReason(clip.id, 'facebook')}
                publishMediaKinds={getPublishMediaKinds(clip.id)}
                showEditWithAi={hasAiAssistant}
                hasProFeatures={hasProFeatures}
              />
            ))}
            {visibleClips.length === 0 && (
              <div className="col-span-full text-center text-sm text-muted-foreground">
                {t('clips.noClipsFound')}
              </div>
            )}
            {isFetchingMore && (
              <div className="col-span-full text-center text-xs text-muted-foreground py-2">{t('clips.loadingMore')}</div>
            )}
          </div>
        )}
        <ClipPublishDialogs
          showPublishProGate={showPublishProGate}
          onPublishProGateChange={setShowPublishProGate}
          publishTarget={publishTarget}
          onPublishTargetChange={setPublishTarget}
          resolveClipTitle={resolveClipTitle}
          onPublished={refetchPublishes}
        />
        {queueTargetClipId ? (
          <SendToQueueDialog
            open
            clipId={queueTargetClipId}
            clipTitle={resolveClipTitle(queueTargetClipId)}
            onOpenChange={setQueueDialogOpen}
          />
        ) : null}
      </div>
    </Layout>
  );
};

const ClipsWrapper = () => {
  const tourSteps = useTourSteps();
  const WrappedComponent = withTour(ClipsContent, 'autoclipper-clips-tour-seen', tourSteps);
  return <WrappedComponent />;
};

const Clips = ClipsWrapper;

export default Clips;
